import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { BrandingProvider } from './contexts/BrandingContext';
import { TenantProvider } from './contexts/TenantContext';
import ForcePasswordResetModal from './components/ForcePasswordResetModal';

// Pages
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import Courses from './pages/Courses';
import CourseDetail from './pages/CourseDetail';
import CoursePreview from './pages/CoursePreview';
import QuizDetail from './pages/QuizDetail';
import LearningPaths from './pages/LearningPaths';
import Leaderboard from './pages/Leaderboard';
import Achievements from './pages/Achievements';
import Discussions from './pages/Discussions';
import Recognition from './pages/Recognition';
import Compliance from './pages/Compliance';
import ManagerDashboard from './pages/ManagerDashboard';
import Profile from './pages/Profile';
import AdminSettings from './pages/AdminSettings';
import UserManagement from './pages/UserManagement';
import CourseManagement from './pages/CourseManagement';
import LearningAnalytics from './pages/LearningAnalytics';
import MyTeam from './pages/MyTeam';
import AIVideoLibrary from './pages/AIVideoLibrary';
import SalesAdmin from './pages/SalesAdmin';
import LicenseActivation from './pages/LicenseActivation';
import DailyQuestionManager from './pages/DailyQuestionManager';
// Lazy load large components
const OrgStructure = React.lazy(() => import('./pages/OrgStructure'));

// Protected Route Component
const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (adminOnly && user.role !== 'admin') {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Public Route (redirect if logged in)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Manager Route (only for managers and admins)
const ManagerRoute = ({ children }) => {
  const { user } = useAuth();
  const allowedRoles = ['store_manager', 'regional_manager', 'admin', 'training_team'];

  if (!allowedRoles.includes(user?.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />
      <Route
        path="/register"
        element={
          <PublicRoute>
            <Register />
          </PublicRoute>
        }
      />
      
      {/* License Activation - Public Route */}
      <Route path="/activate" element={<LicenseActivation />} />

      {/* Protected Routes */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/courses"
        element={
          <ProtectedRoute>
            <Courses />
          </ProtectedRoute>
        }
      />
      <Route
        path="/courses/:id"
        element={
          <ProtectedRoute>
            <CourseDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/quiz/:id"
        element={
          <ProtectedRoute>
            <QuizDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/learning-paths"
        element={
          <ProtectedRoute>
            <LearningPaths />
          </ProtectedRoute>
        }
      />
      <Route
        path="/leaderboard"
        element={
          <ProtectedRoute>
            <Leaderboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/achievements"
        element={
          <ProtectedRoute>
            <Achievements />
          </ProtectedRoute>
        }
      />
      <Route
        path="/discussions"
        element={
          <ProtectedRoute adminOnly>
            <Discussions />
          </ProtectedRoute>
        }
      />
      <Route
        path="/recognition"
        element={
          <ProtectedRoute>
            <Recognition />
          </ProtectedRoute>
        }
      />
      <Route
        path="/compliance"
        element={
          <ProtectedRoute>
            <Compliance />
          </ProtectedRoute>
        }
      />
      <Route
        path="/manager"
        element={
          <ProtectedRoute>
            <ManagerRoute>
              <ManagerDashboard />
            </ManagerRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/my-team"
        element={
          <ProtectedRoute>
            <ManagerRoute>
              <MyTeam />
            </ManagerRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <ManagerRoute>
              <AdminSettings />
            </ManagerRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            <ManagerRoute>
              <UserManagement />
            </ManagerRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/daily-questions"
        element={
          <ProtectedRoute>
            <ManagerRoute>
              <DailyQuestionManager />
            </ManagerRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/course-management"
        element={
          <ProtectedRoute>
            <ManagerRoute>
              <CourseManagement />
            </ManagerRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/ai-videos"
        element={
          <ProtectedRoute>
            <ManagerRoute>
              <AIVideoLibrary />
            </ManagerRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/course-preview/:courseId"
        element={
          <ProtectedRoute>
            <ManagerRoute>
              <CoursePreview />
            </ManagerRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/analytics"
        element={
          <ProtectedRoute>
            <ManagerRoute>
              <LearningAnalytics />
            </ManagerRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/org-structure"
        element={
          <ProtectedRoute>
            <ManagerRoute>
              <React.Suspense fallback={<div className="flex items-center justify-center min-h-screen"><div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" /></div>}>
                <OrgStructure />
              </React.Suspense>
            </ManagerRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <Profile />
          </ProtectedRoute>
        }
      />

      {/* Sales Admin Panel - OEM Management */}
      <Route
        path="/sales-admin/*"
        element={<SalesAdmin />}
      />

      {/* Default redirect */}
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <TenantProvider>
          <BrandingProvider>
            <BrowserRouter>
              <AppRoutes />
              <ForcePasswordResetModal />
              <Toaster
                position="top-right"
                richColors
                closeButton
                theme="system"
              />
            </BrowserRouter>
          </BrandingProvider>
        </TenantProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
